/**
 * formatRelativeTime — turn an ISO-8601 timestamp into a compact human
 * "X ago" string for the extension-version widgets (popup version
 * status + Sequences "Published <ago>" line).
 *
 * Pure function, no locale plumbing, English-only per workspace memory
 * `feedback_language_english_for_app_and_slack`. No external dependency
 * — `date-fns` was considered but not pulled in for this single use
 * site (the call surface is two strings; the dep cost would dominate).
 *
 * Granularity matches the PRD's example formats: "just now" / "5m ago"
 * / "3h ago" / "2d ago" / "Apr 12". Anything older than ~7 days falls
 * back to a Mon DD date label so the timeline stays scannable.
 *
 * Defensive on the input:
 *   - empty / null / undefined → empty string (caller decides what to show)
 *   - unparseable → empty string
 *   - future timestamps (clock skew) → "just now"
 */

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Format an ISO-8601 timestamp as a relative-time string.
 *
 * @param iso  An ISO-8601 timestamp (e.g. `"2026-06-17T14:00:00Z"`).
 * @param now  Reference instant. Defaults to `Date.now()` — exposed so
 *             tests can pin a deterministic "now".
 * @returns    A short label ("3h ago", "Apr 12") or `""` if the input
 *             could not be parsed.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";

  const diff = now - t;
  if (diff < MINUTE_MS) return "just now";
  if (diff < HOUR_MS) {
    const m = Math.floor(diff / MINUTE_MS);
    return `${m}m ago`;
  }
  if (diff < DAY_MS) {
    const h = Math.floor(diff / HOUR_MS);
    return `${h}h ago`;
  }
  if (diff < WEEK_MS) {
    const d = Math.floor(diff / DAY_MS);
    return `${d}d ago`;
  }

  // Older than a week — render as "Mon DD" (locale-fixed: English short
  // month names per workspace memory).
  const date = new Date(t);
  const month = MONTHS[date.getUTCMonth()];
  const day = date.getUTCDate();
  return `${month} ${day}`;
}
